import { Api, ApiRequest, ResType } from '@api'
import { Logger } from '@utils'
import {
	ShipmentFedexCreationType,
	DispatchCreationType,
	DispatchUpdateType,
} from '@schemas'

/** FedEx 라벨 목록 — 주문 자세히 모달이 `orderId` 로 그 주문의 라벨만 읽는다 */
export const getShipment = async ({
	query = {} as {
		status?: string
		keyword?: string
		orderId?: string
		startDate?: string
		endDate?: string
		seasonId?: string
	},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'get',
			uri: '/shipment',
			query: query,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/**
 * 배송 관리 = 출고 대장 목록 — **행 = 배송 건(박스)** (DISPATCH_PLAN §3).
 * 기준은 월(주문일) | 기간(주문일) | 시즌 중 하나만 보낸다.
 */
export const getDispatchBoard = async ({
	query = {} as {
		status?: string
		keyword?: string
		month?: string
		startDate?: string
		endDate?: string
		seasonId?: string
		kind?: string
		channel?: string
		orderId?: string
	},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'get',
			uri: '/shipment/board',
			query: query,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 자세히 한 장 — 배송 건 · 품목 줄 · 박스 · 라벨 이력 · 라벨 폼 초기값 (`/main/shipping/:dispatchId`) */
export const getDispatchOne = async ({
	params = { dispatchId: '' } as { dispatchId: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'get',
			uri: `/shipment/board/${encodeURIComponent(params.dispatchId)}`,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 수동 배송 건 추가 — 응답 `[{ id }]` */
export const createDispatch = async ({
	body = {} as DispatchCreationType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'post',
			uri: '/shipment/board',
			body: body,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 배송 건 수정 — 주는 칸만 바뀐다 */
export const updateDispatch = async ({
	params = { dispatchId: '' } as { dispatchId: string },
	body = {} as DispatchUpdateType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'put',
			uri: `/shipment/board/${encodeURIComponent(params.dispatchId)}`,
			body: body,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 소프트 삭제 — 주문 건의 박스 1 · 라벨이 붙은 건은 400 */
export const deleteDispatch = async ({
	params = { dispatchId: '' } as { dispatchId: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'delete',
			uri: `/shipment/board/${encodeURIComponent(params.dispatchId)}`,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 박스 추가 — 같은 주문 아래 배송 건 하나 더. 응답 `[{ id }]` */
export const addDispatchBox = async ({
	params = { dispatchId: '' } as { dispatchId: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'post',
			uri: `/shipment/board/${encodeURIComponent(params.dispatchId)}/box`,
			body: {},
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const getShipmentPrefill = async ({
	query = { dispatchId: '' } as { dispatchId: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'get',
			uri: '/shipment/prefill',
			query: query,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const createFedexShipment = async ({
	body = {} as ShipmentFedexCreationType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'post',
			uri: '/shipment/fedex',
			body: body,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const getShipmentLabel = async ({
	params = { shipmentId: '' } as { shipmentId: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'get',
			uri: `/shipment/${params.shipmentId}/label`,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const trackShipment = async ({
	body = {} as { shipmentIds?: string[]; orderIds?: string[]; dispatchIds?: string[] },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'post',
			uri: '/shipment/track',
			body: body,
			customHeaders: customHeaders,
		})
		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

const shipmentApi = {
	getShipment,
	getDispatchBoard,
	getDispatchOne,
	createDispatch,
	updateDispatch,
	deleteDispatch,
	addDispatchBox,
	getShipmentPrefill,
	createFedexShipment,
	getShipmentLabel,
	trackShipment,
}

export default shipmentApi
